import { useStrings } from "features/i18n/useStrings";
import strings from "./FormOutline.strings";
import {
    selectQuestionGroup,
    selectEnableReportingViewRedirects,
} from "features/qualityForm/qualityFormSlice";
import {
    selectForm,
    selectQuestion,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { LevelContext } from "features/qualityForm/common/Contexts";
import { useContext } from "react";
import "./FormOutline.less";
import { TreeNode } from "../common/components/TreeNode";
import {
    QuestionGroupSettingsIndicators,
    QuestionSettingsIndicators,
} from "features/qualityForm/common/questionComponents/SettingsIndicators";
import { useAppSelector } from "app/hooks";
import {
    selectIsQuestionGroupValid,
    selectIsQuestionValid,
} from "features/qualityForm/qualityFormValidations";
import {
    GuxTruncate,
    GuxButton,
    GuxIcon,
} from "genesys-spark-components-react";
import { MarkdownInline } from "features/common/Markdown";

function QuestionOutlineSummary({ id }: { id: string }) {
    const t = useStrings(strings);
    const questionText = useAppSelector(
        (state) => selectQuestion(state, id)?.text ?? t.labels.newQuestion
    );

    return (
        <div className="content-box-data">
            <div className="question-content-text">
                <span className="small-body">
                    <GuxTruncate max-lines={15}>
                        <MarkdownInline
                            markdownInlineText={questionText}
                        ></MarkdownInline>
                    </GuxTruncate>
                </span>
            </div>
            <QuestionSettingsIndicators questionId={id} />
        </div>
    );
}

function QuestionGroupOutlineSummary({ id }: { id: string }) {
    const t = useStrings(strings);

    const name =
        useAppSelector((state) => selectQuestionGroup(state, id)?.name) ??
        t.labels.newQuestionGroup;
    const numQuestions = useAppSelector(
        (state) => selectQuestionGroup(state, id)?.questions?.length ?? 0
    );

    return (
        <div className="content-box-data">
            <div className="group-content-text">
                <span className="small-body">
                    <GuxTruncate max-lines={1}>
                        <MarkdownInline
                            markdownInlineText={name}
                        ></MarkdownInline>
                    </GuxTruncate>
                </span>
                <div className="light-font">
                    {t.labels.questionCount({ questionCount: numQuestions })}
                </div>
            </div>
            <QuestionGroupSettingsIndicators questionGroupId={id} />
        </div>
    );
}
function QuestionOutline({ id }: { id: string }) {
    const { level, subLevel } = useContext(LevelContext);

    const isQuestionValid = useAppSelector((state) =>
        selectIsQuestionValid(state, id)
    );
    const canViewQuestionDetailsView = useAppSelector(
        selectEnableReportingViewRedirects
    );
    const questionOutline = (
        <QuestionOutlineSummary id={id}></QuestionOutlineSummary>
    );

    const t = useStrings(strings);

    const questionDetailsViewButton = (
        <GuxButton aria-label={t.labels.icon}>
            <GuxIcon iconName="file-report" decorative={true}></GuxIcon>
        </GuxButton>
    );

    return (
        <TreeNode
            index={t.indices.questionIndex({
                level: level,
                subIndex: subLevel,
            })}
            treeNodeCustomContent={questionOutline}
            {...(canViewQuestionDetailsView && {
                treeNodeCustomIcon: questionDetailsViewButton,
            })}
            hasCustomIcon={canViewQuestionDetailsView}
            hasError={!isQuestionValid}
        />
    );
}

function QuestionGroupOutline({ id }: { id: string }) {
    const { level } = useContext(LevelContext);
    const questionIds =
        useAppSelector((state) => selectQuestionGroup(state, id)?.questions) ??
        [];
    const isQuestionGroupValid = useAppSelector((state) =>
        selectIsQuestionGroupValid(state, id)
    );
    const canViewQuestionDetailsView = useAppSelector(
        selectEnableReportingViewRedirects
    );

    const questions = questionIds.map((questionId, index) => {
        const questionOutline = (
            <LevelContext.Provider
                value={{ level: level, subLevel: index + 1 }}
            >
                <QuestionOutline id={questionId}></QuestionOutline>
            </LevelContext.Provider>
        );
        return (
            <div
                className="QuestionOutline"
                key={questionId}
                data-testid="question-outline"
            >
                {questionOutline}
            </div>
        );
    });

    const questionGroupData = (
        <QuestionGroupOutlineSummary id={id}></QuestionGroupOutlineSummary>
    );

    return (
        <div className="QuestionGroupOutline">
            <TreeNode
                index={level.toString()}
                treeNodeCustomContent={questionGroupData}
                hasCustomIcon={canViewQuestionDetailsView}
                hasError={!isQuestionGroupValid}
            >
                {questions}
            </TreeNode>
        </div>
    );
}

export function FormOutline() {
    const questionGroupIds =
        useAppSelector((state) => selectForm(state)?.questionGroups) ?? [];
    const questionGroups = questionGroupIds.map((questionGroupId, index) => {
        return (
            <LevelContext.Provider
                key={questionGroupId}
                value={{ level: index + 1, subLevel: 0 }}
            >
                <QuestionGroupOutline
                    id={questionGroupId}
                    key="questionGroupId"
                ></QuestionGroupOutline>
            </LevelContext.Provider>
        );
    });
    return (
        <div className="FormOutline" data-testid="form-outline" role="tree">
            {questionGroups}
        </div>
    );
}
